import { useEffect, useRef, useState, useCallback } from "react";
import * as signalR from "@microsoft/signalr";

const HUB_URL = `${process.env.REACT_APP_API}/notifications`;

export const useSignalR = (token, options = {}) => {
	const { onConnected, onDisconnected, onError, onNotification } = options;

	const connectionRef = useRef(null);
	const [status, setStatus] = useState("DISCONNECTED");

	const connect = useCallback(async () => {
		if (!token) return;
		if (connectionRef.current) return;

		setStatus("CONNECTING");

		const connection = new signalR.HubConnectionBuilder()
			.withUrl(HUB_URL, {
				accessTokenFactory: () => token,
				transport: signalR.HttpTransportType.WebSockets, // 🔥 важно
			})
			.withAutomaticReconnect({
				nextRetryDelayInMilliseconds: retryContext => {
					if (retryContext.previousRetryCount >= 5) {
						return null; // stop reconnecting
					}
					return Math.min(3000 * (retryContext.previousRetryCount + 1), 10000);
				},
			})
			.configureLogging(signalR.LogLevel.Information)
			.build();

		connectionRef.current = connection;

		// 🔔 событие уведомлений
		connection.on("ReceiveNotification", data => {
			onNotification?.(data);
		});

		connection.onreconnecting(() => {
			setStatus("RECONNECTING");
		});

		connection.onreconnected(() => {
			setStatus("CONNECTED");
		});

		connection.onclose(error => {
			setStatus("DISCONNECTED");
			connectionRef.current = null;
			onDisconnected?.(error);
		});

		try {
			await connection.start();
			setStatus("CONNECTED");
			onConnected?.();
		} catch (err) {
			console.error("SignalR connection error:", err);
			setStatus("ERROR");
			connectionRef.current = null;
			onError?.(err);
		}
	}, [token, onConnected, onDisconnected, onError, onNotification]);

	const disconnect = useCallback(async () => {
		if (connectionRef.current) {
			await connectionRef.current.stop();
			connectionRef.current = null;
			setStatus("DISCONNECTED");
		}
	}, []);

	const invoke = useCallback(async (method, payload) => {
		if (!connectionRef.current) return;

		try {
			return await connectionRef.current.invoke(method, payload);
		} catch (err) {
			console.error("Invoke error:", err);
			throw err;
		}
	}, []);

	useEffect(() => {
		connect();
		return () => {
			disconnect();
		};
	}, [connect, disconnect]);

	return {
		status,
		invoke,
		reconnect: connect,
		disconnect,
	};
};
